/* Belfast Whiskey Week — shared "lineup cart" festival week drawer.
 *
 * Consumers: assets/walking-tours-listing.js, assets/distillery-days-listing.js
 * and the inline script in sections/collection.liquid. This file must be loaded
 * before those consumers run their drawer setup (it is loaded with `defer`
 * ahead of the deferred listing scripts; the collection inline script waits
 * for DOMContentLoaded before initialising the drawer).
 *
 * window.BWWLineupCart.init(config) → api or null (when the drawer markup,
 * #lineup-cart-root, is not on the page).
 *
 * config:
 *   money           function(cents) → display string. Falls back to an
 *                   internal GBP/Shopify-currency formatter if omitted.
 *   esc             optional HTML escaper (defaults to internal).
 *   productMeta     optional { productId: { sort, label, venue } } map used
 *                   to group cart lines by festival day.
 *   festivalStart   optional 'YYYY-MM-DD' — first day shown in the day strip.
 *   festivalEnd     optional 'YYYY-MM-DD' — last day shown in the day strip.
 *   stripNav        optional; pass false to disable click-to-scroll on the
 *                   day strip (default true).
 *   emptyHTML       optional HTML for the empty-cart state.
 *   onEmptyRendered optional function(drawerBody, api) called after the
 *                   empty state is injected (e.g. to wire a "Browse" CTA).
 *   loadErrorHTML   optional HTML shown when /cart.js can't be fetched.
 *   addProperties   optional line-item properties object sent with
 *                   /cart/add.js (e.g. { _wt: '1' } for walking tours).
 *   afterAdd        optional function(result) → result/promise, runs after a
 *                   successful add (e.g. walking-tours discount re-sync).
 *   dayGapNudge     optional { url, label } — when set, festival days with no
 *                   items that fall between the buyer's first and last booked
 *                   days get a slim "Nothing on {Mon 27} yet" row linking to
 *                   url + '?day=' + <day slug, e.g. mon-27-jul>. Off when
 *                   omitted (walking-tours / distillery-days unaffected).
 *   upsell          optional { matchHandles, excludeHandles, html } — after
 *                   rendering, if any cart item's product handle is in
 *                   matchHandles and none is in excludeHandles, html (trusted,
 *                   consumer-provided) is appended in .lineup-cart__upsell.
 *                   Off when omitted.
 *
 * api: { open, close, refresh, addVariant(variantId, quantity), renderCart,
 *        updateCounts }
 */
(function () {
  function defaultEsc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function defaultMoney() {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    var moneyFmt;
    try { moneyFmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency }); }
    catch (e) { moneyFmt = null; }
    return function (cents) {
      var v = moneyFmt ? moneyFmt.format(cents / 100) : '£' + (cents / 100).toFixed(2);
      return v.replace(/\.00($|\s)/, '$1');
    };
  }

  function init(config) {
    config = config || {};

    var drawerRoot = document.getElementById('lineup-cart-root');
    if (!drawerRoot) return null;
    /* Already initialised (by a section or the header cart button): reuse it
       so listeners aren't bound twice. First caller's config wins. */
    if (drawerRoot.__bwwLineupApi) return drawerRoot.__bwwLineupApi;

    var drawer = document.getElementById('lineup-cart');
    var drawerBody = document.getElementById('lineup-cart-body');
    var drawerFoot = document.getElementById('lineup-cart-foot');
    var drawerSubtotal = document.getElementById('lineup-cart-subtotal');
    var drawerStrip = document.getElementById('lineup-cart-strip');
    var overlay = document.getElementById('lineup-cart-overlay');
    var closeBtn = document.getElementById('lineup-cart-close');
    var lastFocus = null;

    var esc = config.esc || defaultEsc;
    var money = config.money || defaultMoney();
    var productMeta = config.productMeta || {};
    var emptyHTML = config.emptyHTML ||
      '<div class="lineup-cart__empty"><p>Your week is wide open.</p>' +
      '<p>Add tickets to start planning your week.</p></div>';
    var loadErrorHTML = config.loadErrorHTML ||
      '<p class="lineup-cart__empty">Couldn’t load your cart.</p>';

    /* Escape the section's stacking context (body > * gets z-index: 1) —
       same pattern as the festival widget and product sticky bar. */
    if (drawerRoot.parentElement !== document.body) {
      document.body.appendChild(drawerRoot);
      drawerRoot.style.position = 'relative';
      drawerRoot.style.zIndex = '2147483646';
    }

    function updateCounts(n) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count], [data-lineup-count]'), function (el) {
        el.textContent = n;
        el.classList.toggle('is-empty', n === 0);
      });
    }

    /* Festival day strip: one cell per day of the festival */
    var stripDays = [];
    (function () {
      var start = new Date(config.festivalStart + 'T00:00:00Z');
      var end = new Date(config.festivalEnd + 'T00:00:00Z');
      if (isNaN(start) || isNaN(end)) return;
      var dows = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
      var mons = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      for (var d = new Date(start); d <= end && stripDays.length < 16; d.setUTCDate(d.getUTCDate() + 1)) {
        var key = d.toISOString().slice(0, 10).replace(/-/g, '');
        var dow = dows[d.getUTCDay()];
        var num = d.getUTCDate();
        stripDays.push({
          key: key,
          dow: dow,
          num: num,
          /* short label ("Mon 27") + ?day= slug ("mon-27-jul") matching the
             line-up's "Fri 24 Jul"-style day filter values */
          gapLabel: dow.charAt(0) + dow.slice(1).toLowerCase() + ' ' + num,
          param: dow.toLowerCase() + '-' + (num < 10 ? '0' : '') + num + '-' + mons[d.getUTCMonth()]
        });
      }
    })();

    function renderStrip(groups) {
      if (!drawerStrip) return;
      if (!stripDays.length) { drawerStrip.hidden = true; return; }
      var counts = {};
      groups.forEach(function (g) {
        if (g.sort) counts[g.sort] = g.items.reduce(function (n, it) { return n + it.quantity; }, 0);
      });
      drawerStrip.innerHTML = stripDays.map(function (day) {
        var n = counts[day.key] || 0;
        return '<button type="button" class="lineup-cart__day' + (n ? ' has-items' : '') + '" data-day="' + day.key + '"' +
          (n ? '' : ' tabindex="-1" aria-hidden="true"') + '>' +
          '<span class="lineup-cart__day-dow">' + day.dow + '</span>' +
          '<span class="lineup-cart__day-num">' + day.num + '</span>' +
          '<span class="lineup-cart__day-dot">' + (n || '·') + '</span></button>';
      }).join('');
    }

    if (drawerStrip && config.stripNav !== false) {
      drawerStrip.addEventListener('click', function (e) {
        var day = e.target.closest('.lineup-cart__day.has-items');
        if (!day) return;
        var group = drawerBody.querySelector('[data-group="' + day.getAttribute('data-day') + '"]');
        if (group) group.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    function groupCart(cartData) {
      var byKey = {};
      cartData.items.forEach(function (item) {
        var meta = productMeta[String(item.product_id) + ':' + String(item.variant_id)]
          || productMeta[String(item.product_id)]
          || { sort: '', label: '', venue: '' };
        var sort = meta.sort;
        var label = meta.label || 'Anytime that week';
        var key = sort || 'zzz-' + label;
        if (!byKey[key]) byKey[key] = { sort: sort, label: label, items: [] };
        byKey[key].items.push(item);
      });
      return Object.keys(byKey).sort().map(function (k) { return byKey[k]; });
    }

    /* Config-gated (dayGapNudge): slim rows for festival days with nothing
       booked that sit between the buyer's first and last booked days. */
    function renderGapNudges(groups) {
      var cfg = config.dayGapNudge;
      if (!cfg || !cfg.url || !stripDays.length) return '';
      var booked = {};
      groups.forEach(function (g) { if (g.sort) booked[g.sort] = true; });
      var keys = Object.keys(booked).sort();
      if (keys.length < 2) return '';
      var first = keys[0];
      var last = keys[keys.length - 1];
      var rows = stripDays.filter(function (day) {
        return day.key > first && day.key < last && !booked[day.key];
      }).map(function (day) {
        var href = cfg.url + (cfg.url.indexOf('?') > -1 ? '&' : '?') + 'day=' + encodeURIComponent(day.param);
        return '<div class="lineup-cart__gap">' +
          '<span class="lineup-cart__gap-text">Nothing on ' + esc(day.gapLabel) + ' yet</span>' +
          '<a class="lineup-cart__gap-link" href="' + esc(href) + '">' + esc(cfg.label || 'See what’s on') + '</a>' +
        '</div>';
      }).join('');
      return rows ? '<div class="lineup-cart__gaps">' + rows + '</div>' : '';
    }

    /* Config-gated (upsell): consumer-provided HTML block shown when the cart
       holds a matching product and no excluded one. */
    function renderUpsell(cartData) {
      var cfg = config.upsell;
      if (!cfg || !cfg.html) return '';
      function hasHandle(handles) {
        if (!handles || !handles.length) return false;
        return cartData.items.some(function (item) {
          return handles.indexOf(item.handle) > -1;
        });
      }
      if (!hasHandle(cfg.matchHandles) || hasHandle(cfg.excludeHandles)) return '';
      return '<div class="lineup-cart__upsell">' + cfg.html + '</div>';
    }

    /* "'Mon in, you're saving a tenner" strip at the top of the drawer,
       above the scrolling list; pops when the saving grows. */
    function renderSavings(saved) {
      var savings = document.getElementById('lineup-cart-savings');
      if (!savings) {
        if (!saved) return;
        savings = document.createElement('p');
        savings.id = 'lineup-cart-savings';
        savings.className = 'lineup-cart__savings';
        drawerBody.parentNode.insertBefore(savings, drawerBody);
      }
      savings.hidden = !saved;
      if (saved) {
        /* £10 → "a tenner", £20 → "two tenners" … otherwise the amount. */
        var tens = saved % 1000 === 0 ? saved / 1000 : 0;
        var amount = tens === 1 ? 'a tenner'
          : tens > 1 && tens < 7 ? ['two', 'three', 'four', 'five', 'six'][tens - 2] + ' tenners'
          : money(saved);
        savings.innerHTML = '<span class="lineup-cart__savings-emoji" aria-hidden="true">🥃👀</span>' +
          '<span>’Mon in, you’re saving <strong>' + esc(amount) + '</strong></span>';
        if (saved > (savings.__saved || 0)) {
          savings.classList.remove('is-pop');
          void savings.offsetWidth;
          savings.classList.add('is-pop');
        }
      }
      savings.__saved = saved;
    }

    function renderCart(cartData) {
      updateCounts(cartData.item_count);

      if (!cartData.items.length) {
        drawerBody.innerHTML = emptyHTML;
        drawerFoot.hidden = true;
        renderSavings(0);
        renderStrip([]);
        if (config.onEmptyRendered) config.onEmptyRendered(drawerBody, api);
        return;
      }

      var groups = groupCart(cartData);
      renderStrip(groups);

      drawerBody.innerHTML = groups.map(function (g) {
        return '<section class="lineup-cart__group"' + (g.sort ? ' data-group="' + g.sort + '"' : '') + '>' +
          '<h3 class="lineup-cart__group-head">' + esc(g.label) + '</h3>' +
          g.items.map(function (item) {
            var imgUrl = item.featured_image && item.featured_image.url;
            var img = imgUrl
              ? '<img class="lineup-cart__item-img" src="' + esc(imgUrl + (imgUrl.indexOf('?') > -1 ? '&' : '?') + 'width=160') + '" alt="" loading="lazy">'
              : '<span class="lineup-cart__item-img" aria-hidden="true"></span>';
            var variant = item.variant_title && item.variant_title !== 'Default Title'
              ? '<p class="lineup-cart__item-variant">' + esc(item.variant_title) + '</p>' : '';
            var was = item.original_line_price > item.final_line_price
              ? '<s class="lineup-cart__item-was">' + money(item.original_line_price) + '</s>' : '';
            return '<div class="lineup-cart__item" data-key="' + esc(item.key) + '">' +
              img +
              '<div>' +
                '<p class="lineup-cart__item-title"><a href="' + esc(item.url) + '">' + esc(item.product_title) + '</a></p>' +
                variant +
                '<span class="lineup-cart__item-qty">' +
                  '<button type="button" class="lineup-cart__qty-btn" data-qty-change="-1" aria-label="Decrease quantity">−</button>' +
                  '<span class="lineup-cart__qty-num">' + item.quantity + '</span>' +
                  '<button type="button" class="lineup-cart__qty-btn" data-qty-change="1" aria-label="Increase quantity">+</button>' +
                '</span>' +
              '</div>' +
              '<div class="lineup-cart__item-end">' +
                '<span class="lineup-cart__item-price">' + was + (item.final_line_price === 0 ? 'Free' : money(item.final_line_price)) + '</span>' +
                '<button type="button" class="lineup-cart__remove" data-remove>Remove</button>' +
              '</div>' +
            '</div>';
          }).join('') +
        '</section>';
      }).join('') + renderGapNudges(groups) + renderUpsell(cartData);

      var total = cartData.total_price != null ? cartData.total_price : cartData.items_subtotal_price;
      var saved = cartData.total_discount || 0;
      drawerSubtotal.innerHTML = (saved > 0 && cartData.original_total_price > total
        ? '<s class="lineup-cart__subtotal-was">' + esc(money(cartData.original_total_price)) + '</s> ' : '') + esc(money(total));

      renderSavings(saved);
      drawerFoot.hidden = false;
    }

    function fetchCart() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
    }

    function refreshDrawer() {
      return fetchCart().then(renderCart).catch(function () {
        drawerBody.innerHTML = loadErrorHTML;
      });
    }

    function openDrawer() {
      lastFocus = document.activeElement;
      drawerRoot.hidden = false;
      requestAnimationFrame(function () { drawerRoot.classList.add('is-open'); });
      document.documentElement.style.overflow = 'hidden';
      refreshDrawer().then(function () { drawer.focus(); });
    }

    function closeDrawer() {
      drawerRoot.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      setTimeout(function () { drawerRoot.hidden = true; }, 320);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !drawerRoot.hidden) closeDrawer();
    });

    /* Quantity change / remove */
    drawerBody.addEventListener('click', function (e) {
      var item = e.target.closest('.lineup-cart__item');
      if (!item) return;
      var key = item.getAttribute('data-key');
      var qty = null;

      var qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        var current = parseInt(item.querySelector('.lineup-cart__qty-num').textContent, 10) || 1;
        qty = Math.max(0, current + parseInt(qtyBtn.getAttribute('data-qty-change'), 10));
      }
      if (e.target.closest('[data-remove]')) qty = 0;
      if (qty === null) return;

      drawerRoot.classList.add('is-loading');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(
              function (err) { throw err; },
              function () { throw new Error('change failed'); }
            );
          }
          return r.json();
        })
        .then(function () {
          /* Walking-tour lines can live in this drawer too — keep their
             BOGO/group discount code in step after any quantity change. */
          if (window.WalkingToursPricing && window.WalkingToursPricing.syncCartDiscounts) {
            return window.WalkingToursPricing.syncCartDiscounts();
          }
        })
        .then(function () { return fetchCart(); })
        .then(renderCart)
        .catch(function (err) {
          return refreshDrawer().then(function () {
            var msg = err && err.description;
            if (msg && typeof msg === 'string') {
              var note = document.createElement('p');
              note.className = 'lineup-cart__empty';
              note.textContent = '⚠ ' + msg;
              drawerBody.insertBefore(note, drawerBody.firstChild);
            }
          });
        })
        .then(function () { drawerRoot.classList.remove('is-loading'); });
    });

    /* Add a variant to the cart */
    function addVariant(variantId, quantity) {
      quantity = quantity || 1;
      var payload = { id: variantId, quantity: quantity };
      if (config.addProperties) payload.properties = config.addProperties;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      }).then(function (result) {
        if (config.afterAdd) return config.afterAdd(result);
        return result;
      });
    }

    var api = {
      open: openDrawer,
      close: closeDrawer,
      refresh: refreshDrawer,
      addVariant: addVariant,
      renderCart: renderCart,
      updateCounts: updateCounts
    };
    drawerRoot.__bwwLineupApi = api;
    return api;
  }

  window.BWWLineupCart = window.BWWLineupCart || {};
  window.BWWLineupCart.init = init;
})();
